import React, { ReactNode, useMemo } from 'react';
import { Table, TableBody, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { LeaderboardRankIcon } from './LeaderboardRankIcon';
import { LeaderboardScoreDisplay } from './LeaderboardScoreDisplay';
import { LeaderboardExpandableRow } from './LeaderboardExpandableRow';
import { VirtualScrollList } from '@/components/shared/VirtualScrollList';
import type { VirtualScrollItem } from '@/components/shared/VirtualScrollList';

// Premium Color Palette from PREMIUM_METRIC_PATTERNS.md
const PREMIUM_COLORS = {
  primaryGolfGreen: '#1B4332',
  primaryBackgroundCream: '#FDF8F0',
  secondaryGolfGreen: '#2D5A3D',
  accentBrightGreen: '#52B788',
  accentDeepGreen: '#047857',
  backgroundMint: '#F0FDF4',
  cardWhite: 'rgba(255, 255, 255, 0.95)',
  successGreen: '#16A34A',
  errorRed: '#DC2626',
  neutralGray: '#9CA3AF',
  darkText: '#374151',
  goldAccent: '#F59E0B',
  silverAccent: '#6B7280',
  bronzeAccent: '#EA580C'
};

// Premium styling system
const PREMIUM_STYLES = {
  glassMorphCard: {
    background: 'rgba(255, 255, 255, 0.95)',
    backdropFilter: 'blur(20px)',
    border: '1px solid rgba(255, 255, 255, 0.2)',
    borderRadius: '24px',
    boxShadow: '0 8px 32px rgba(27, 67, 50, 0.1)'
  }
};

export interface LeaderboardColumn {
  key: string;
  label: string;
  sortable?: boolean;
  width?: string;
  align?: 'left' | 'center' | 'right';
  render?: (value: any, entry: LeaderboardEntry) => ReactNode;
}

export interface LeaderboardEntry {
  id: string;
  rank: number;
  name: string;
  score: number;
  scoreDifferential?: number;
  avatar?: string;
  isCurrentUser?: boolean;
  expandedContent?: ReactNode;
  metadata?: Record<string, any>;
}

export interface LeaderboardBaseProps {
  entries: LeaderboardEntry[];
  columns: LeaderboardColumn[];
  title?: string;
  subtitle?: string;
  loading?: boolean;
  error?: string | null;
  emptyMessage?: string;
  showRankIcons?: boolean;
  allowExpansion?: boolean;
  onEntryClick?: (entry: LeaderboardEntry) => void;
  onSort?: (columnKey: string, direction: 'asc' | 'desc') => void;
  headerActions?: ReactNode;
  className?: string;
  variant?: 'default' | 'premium' | 'compact';
}

/**
 * Unified Leaderboard Base Component
 * 
 * Consolidates all leaderboard functionality into a single reusable component:
 * - Consistent ranking display with trophy icons
 * - Unified score formatting and color coding
 * - Expandable rows with smooth animations
 * - Premium golf styling system
 * - Responsive mobile-first design
 * - Proper loading and error states
 */
export function LeaderboardBase({
  entries,
  columns,
  title,
  subtitle,
  loading = false,
  error = null,
  emptyMessage = "No data available",
  showRankIcons = true,
  allowExpansion = false,
  onEntryClick,
  onSort,
  headerActions,
  className = "",
  variant = 'default'
}: LeaderboardBaseProps) {

  // Handle loading state
  if (loading) {
    return (
      <div className={`premium-card ${className}`} style={PREMIUM_STYLES.glassMorphCard}>
        <div className="p-6 text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 mx-auto mb-4" 
            style={{ borderColor: PREMIUM_COLORS.primaryGolfGreen }}
          />
          <p className="text-sm" style={{ color: PREMIUM_COLORS.secondaryGolfGreen }}>
            Loading leaderboard...
          </p>
        </div>
      </div>
    );
  }

  // Handle error state
  if (error) {
    return (
      <div className={`premium-card ${className}`} style={PREMIUM_STYLES.glassMorphCard}>
        <div className="p-6 text-center">
          <div className="w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-4"
            style={{ backgroundColor: 'rgba(220, 38, 38, 0.1)' }}
          >
            <span className="text-red-500 text-xl">⚠</span>
          </div>
          <h3 className="text-lg font-semibold mb-2" style={{ color: PREMIUM_COLORS.primaryGolfGreen }}>
            Error Loading Leaderboard
          </h3>
          <p className="text-sm" style={{ color: PREMIUM_COLORS.secondaryGolfGreen }}>
            {error}
          </p>
        </div>
      </div>
    );
  }

  // Handle empty state
  if (!entries || entries.length === 0) {
    return (
      <div className={`premium-card ${className}`} style={PREMIUM_STYLES.glassMorphCard}>
        <div className="p-6 text-center">
          <div className="w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-4"
            style={{ backgroundColor: 'rgba(156, 163, 175, 0.1)' }}
          >
            <span className="text-gray-400 text-xl">🏆</span>
          </div>
          <h3 className="text-lg font-semibold mb-2" style={{ color: PREMIUM_COLORS.primaryGolfGreen }}>
            No Results Yet
          </h3>
          <p className="text-sm" style={{ color: PREMIUM_COLORS.secondaryGolfGreen }}>
            {emptyMessage}
          </p>
        </div>
      </div>
    );
  }

  const getVariantStyles = () => {
    switch (variant) {
      case 'premium':
        return {
          card: PREMIUM_STYLES.glassMorphCard,
          spacing: 'p-6'
        };
      case 'compact':
        return {
          card: { 
            background: PREMIUM_COLORS.backgroundMint,
            borderRadius: '12px',
            border: `1px solid rgba(27, 67, 50, 0.1)`
          },
          spacing: 'p-4'
        };
      default:
        return {
          card: {
            background: 'white',
            borderRadius: '8px',
            border: '1px solid #e5e7eb'
          },
          spacing: 'p-4'
        };
    }
  };

  const variantStyles = getVariantStyles();

  // Convert entries to virtual scroll items for large datasets
  const virtualLeaderboardItems: VirtualScrollItem[] = useMemo(() => {
    return entries.map(entry => ({
      id: entry.id,
      height: 60, // Standard row height
      data: entry
    }));
  }, [entries]);

  return (
    <div className={`${className}`} style={variantStyles.card}>
      <div className={variantStyles.spacing}>
        {/* Header Section */}
        {(title || subtitle || headerActions) ? <div className="flex items-center justify-between mb-6">
          <div>
            {title ? <h2 className="text-xl font-bold mb-1" style={{ color: PREMIUM_COLORS.primaryGolfGreen }}>
              {title}
                     </h2> : null}
            {subtitle ? <p className="text-sm" style={{ color: PREMIUM_COLORS.secondaryGolfGreen }}>
              {subtitle}
                        </p> : null}
          </div>
          {headerActions ? <div className="flex items-center gap-2">
            {headerActions}
          </div> : null}
                                                </div> : null}

        {/* Leaderboard Table - Virtual Scrolling for Large Datasets */}
        <div className="rounded-xl overflow-hidden border" 
          style={{ borderColor: 'rgba(27, 67, 50, 0.1)' }}
        >
          <Table>
            <TableHeader>
              <TableRow style={{ backgroundColor: PREMIUM_COLORS.backgroundMint }}>
                {showRankIcons ? <TableHead className="w-16 text-center">Rank</TableHead> : null}
                {columns.map((column) => (
                  <TableHead 
                    key={column.key}
                    className={`${column.align === 'center' ? 'text-center' : column.align === 'right' ? 'text-right' : 'text-left'}`}
                    style={{ 
                      width: column.width,
                      color: PREMIUM_COLORS.darkText,
                      fontWeight: '600'
                    }}
                  >
                    {column.sortable ? (
                      <button
                        onClick={() => onSort?.(column.key, 'asc')}
                        className="flex items-center gap-1 hover:opacity-70 transition-opacity"
                        style={{ minHeight: '44px', minWidth: '44px' }}
                      >
                        {column.label}
                        <span className="text-xs">↕</span>
                      </button>
                    ) : (
                      column.label
                    )}
                  </TableHead>
                ))}
                {allowExpansion ? <TableHead className="w-12" /> : null}
              </TableRow>
            </TableHeader>
          </Table>
          
          {/* Virtual Scrolling Body for Performance */}
          {entries.length > 20 ? (
            <VirtualScrollList
              items={virtualLeaderboardItems}
              itemHeight={60}
              containerHeight={400}
              overscan={5}
              renderItem={(item, index) => (
                <div className="border-b border-gray-100">
                  <LeaderboardExpandableRow
                    entry={item.data}
                    columns={columns}
                    showRankIcons={showRankIcons}
                    allowExpansion={allowExpansion}
                    onEntryClick={onEntryClick}
                    isEven={index % 2 === 0}
                  />
                </div>
              )}
              className="bg-white"
            />
          ) : (
            <Table>
              <TableBody>
                {entries.map((entry, index) => (
                  <LeaderboardExpandableRow
                    key={entry.id}
                    entry={entry}
                    columns={columns}
                    showRankIcons={showRankIcons}
                    allowExpansion={allowExpansion}
                    onEntryClick={onEntryClick}
                    isEven={index % 2 === 0}
                  />
                ))}
              </TableBody>
            </Table>
          )}
        </div>

        {/* Footer Info */}
        {entries.length > 0 && (
          <div className="mt-4 text-center">
            <p className="text-xs" style={{ color: PREMIUM_COLORS.neutralGray }}>
              Showing {entries.length} {entries.length === 1 ? 'player' : 'players'}
            </p>
          </div>
        )}
      </div>
    </div>
  );
}